/**
 * Rig animator (AP5.S1) — plays the resolved locomotion clips on built rig
 * avatars and advances them each frame, for the local player and every visible
 * remote alike.
 *
 * Before this, the client had NO animation playback at all: `player-rig-v1`
 * loaded, rendered (O-011/AD-202 fixed its visibility) and then stood in bind
 * pose forever, because the loader discarded `gltf.animations` and nothing ever
 * constructed an `AnimationMixer`. This module is that missing half.
 *
 * AP5.S2 layers one-shot ACTIONS (`RigAction`: attack / hit / dead / gather /
 * craft) over the locomotion state: `playAction` cross-fades a clip in ONCE, and
 * when it completes the animator hands the pose back to whichever locomotion
 * state is current (state changes that arrive mid-action are remembered, not
 * dropped). `dead` clamps on its last frame and holds until an explicit
 * `clearAction` (the respawn), and blocks every other action meanwhile — a corpse
 * neither flinches nor swings. A repeated action restarts its clip (responsive
 * combat). Completion is a callback the mixer fires; a per-record token makes a
 * stale completion (restarted, cleared, cut or superseded) a no-op.
 *
 * MOVEMENT WINS (AP5.S2 fix, 2026-09-02). The server owns position, and the
 * shipped one-shots are long (`Collect_Object` 6.0 s, `Left_Slash` 3.17 s): an
 * action that owned the pose for its whole clip while the entity travelled would
 * slide a crouching/swinging rig across the ground, and a mashed attack would
 * never hand locomotion back. So, per `RigActionPolicy` (data, clips.ts): a
 * transition to a travelling state (`STATE_TRAVELS`) CUTS a non-exempt action
 * straight to locomotion, an action does not START while the entity travels
 * (locomotion keeps the pose), and an action may carry a `holdSeconds` budget
 * that bounds how long it owns a stationary pose regardless of clip length. Only
 * the clamped death is exempt — the server never moves a corpse.
 *
 * Ownership + lifecycle mirror `PlayerRigRenderer`'s per-entity records: one
 * handle per animated entity, `attach` on rig settle, `detach` on despawn,
 * `dispose` on teardown — so a mixer can never outlive its avatar (the leak the
 * E2.3/E2.4 renderers were careful about, applied here too).
 *
 * The concrete `THREE.AnimationMixer` is INJECTED as a `RigMixerFactory`
 * (`createThreeRigMixer`, threeRigMixer.ts) exactly like `GltfLoadFn` is for the
 * loader, so this whole module — and its tests — stay free of `three`/GPU/DOM
 * and run under `node --test` (AD-120).
 *
 * Off the per-tick path (AD-031): nothing here touches movement, collision or
 * the wire. `AnimState` is already a full-state/delta field the server sends
 * (`shared/sim/movement.ts`); this only *presents* it. A clip change happens on
 * a state TRANSITION or an action TRIGGER, never per frame.
 *
 * Import boundary (AD-122): shared types via `@great-cloud/shared/client`.
 */

import type { Object3D } from 'three';

import type { AnimState } from '@great-cloud/shared/client';

import {
  DEFAULT_ACTION_POLICY,
  STATE_TRAVELS,
  hasAnyClip,
  resolveActionClipMap,
  resolveClipFor,
  resolveClipMap,
  type ActionClipMap,
  type ClipMap,
  type RigAction,
  type RigActionPolicy,
} from './clips.js';
import {
  GroundSpeedTracker,
  locomotionRate,
  pickLocomotionClip,
  type LocomotionSpeeds,
} from './locomotionRate.js';

/** The minimal slice of an animation clip the animator needs (name-addressable). */
export interface RigClip {
  name: string;
}

/** Options for a one-shot clip (AP5.S2). */
export interface PlayOnceOptions {
  /**
   * Hold the final frame after the clip ends (death) rather than handing back to
   * the caller — completion still fires, but the caller is expected to keep the
   * pose until it explicitly plays something else.
   */
  clamp?: boolean;
  /**
   * Hold budget, seconds: report completion no later than this far into the clip
   * (the animator's per-action `holdSeconds`). Absent, or longer than the clip,
   * ⇒ the clip's own length. It never lengthens a clip.
   */
  maxSeconds?: number;
  /**
   * Playback rate, as a multiple of the clip's authored speed (the animator's
   * per-action `timeScale`). Absent, or not a finite number > 0, ⇒ 1. It scales
   * the CLOCK, not the budget: `maxSeconds` stays clip seconds, so the same span
   * of clip is shown, faster or slower. A mixer must also scale its fade lead by
   * it, since a fade is wall-clock while the budget is clip time.
   */
  timeScale?: number;
  /**
   * Fired ONCE when the clip completes (its end, or `maxSeconds`). For a
   * non-clamped clip the mixer may fire it up to `fadeSeconds` EARLY so the
   * caller's return cross-fade lands exactly on that point rather than after a
   * visible hold. Must never throw into the mixer; the animator guards its own
   * side.
   */
  onFinished?: () => void;
}

/**
 * A driveable mixer for ONE avatar. The real implementation wraps
 * `THREE.AnimationMixer`; tests inject a recording fake.
 */
export interface RigMixerHandle {
  /** Advance playback. `dtSeconds` — three's `AnimationMixer.update` contract. */
  update(dtSeconds: number): void;
  /** Cross-fade to the LOOPING `clipName` over `fadeSeconds` (a no-op if already current). */
  play(clipName: string, fadeSeconds: number): void;
  /**
   * Play `clipName` ONCE, cross-faded in over `fadeSeconds` on top of whatever is
   * playing (AP5.S2). Calling it again for the clip already running RESTARTS it
   * from frame 0 at full weight (no re-fade — that would dip toward bind pose).
   * A clip this rig does not carry is a no-op: the current pose keeps playing.
   */
  playOnce(clipName: string, fadeSeconds: number, options?: PlayOnceOptions): void;
  /**
   * Set the playback rate of the current LOOPING clip (`q-locomotion-speed-matched-playback`):
   * a multiple of its authored speed. A rate that is not finite and > 0 is ignored. `play()`
   * of another clip resets that clip to rate 1 (the caller re-applies). Optional: a mixer
   * without it simply plays locomotion at the authored rate, as before.
   */
  setLoopRate?(rate: number): void;
  /** Stop playback and release the mixer's cached actions. */
  stop(): void;
}

/**
 * Builds a mixer for an avatar root + its clips, or `null` when this avatar
 * cannot be animated (no clips, or a mixer could not be constructed) — the
 * caller then simply leaves the rig static, never throwing into the frame loop.
 */
export type RigMixerFactory = (root: Object3D, clips: readonly RigClip[]) => RigMixerHandle | null;

/** The walk-only pattern set: which clip a rig walks with (the run is `move`'s own resolution). */
const WALK_ONLY_PATTERNS: Readonly<Record<AnimState, readonly string[]>> = {
  idle: [],
  move: ['walk'],
  dodge: [],
};

/** Default cross-fade, seconds. Short enough to feel responsive, long enough not to pop. */
export const DEFAULT_FADE_SECONDS = 0.18;

export interface RigAnimatorOptions {
  /** Injected mixer construction (real: `createThreeRigMixer`; tests: a fake). */
  createMixer: RigMixerFactory;
  /** Cross-fade duration in seconds (default `DEFAULT_FADE_SECONDS`). */
  fadeSeconds?: number;
  /** Non-fatal warning sink (default `console.warn`). */
  onWarn?: (message: string, error?: unknown) => void;
  /**
   * Per-action movement/hold policy (default `DEFAULT_ACTION_POLICY`). Data: a
   * rig with an upper-body attack mask would flip `attack.interruptedByTravel`
   * here, not in a branch.
   */
  actionPolicy?: Readonly<Record<RigAction, RigActionPolicy>>;
}

/** Per-entity animation record. */
interface AnimRecord {
  mixer: RigMixerHandle;
  clips: ClipMap;
  /** AP5.S2: the one-shot clip per action, resolved once at attach. */
  actionClips: ActionClipMap;
  /**
   * The locomotion state to present. While an action plays this is the state to
   * RETURN to (a change that arrives mid-action is stored here, not played), so a
   * repeated `setState` is still a no-op.
   */
  current: AnimState | null;
  /** AP5.S2: the one-shot action playing (or, for `dead`, holding), else `null`. */
  action: RigAction | null;
  /** The policy the playing action STARTED under (AD-636: a clip override may carry its own). */
  actionPolicy: Readonly<RigActionPolicy> | null;
  /** Every clip name the rig carries — what a named-clip override may ask for. */
  clipNames: ReadonlySet<string>;
  /**
   * AP5.S2: bumped on every action start/clear; a completion callback carrying an
   * older token belongs to a superseded clip and is ignored.
   */
  actionSeq: number;
  /** Authored ground speed per locomotion clip (`AssetEntry.locomotion`); empty ⇒ authored rate. */
  speeds: LocomotionSpeeds;
  /** The rig's walk clip (for the walk-before-run preference), or `null`. */
  walkClip: string | null;
  /** The travelling clip showing while `current` is `move` (the run, or the walk), else `null`. */
  moveShown: string | null;
  /** Smoothed render ground speed. */
  tracker: GroundSpeedTracker;
}

function defaultWarn(message: string, error?: unknown): void {
  console.warn(`[rig-anim] ${message}`, error ?? '');
}

/**
 * Drives animation for every rigged player. Create once per scene, `attach()` a
 * rig as it settles, `setState()` it from the entity's wire `anim`, `update()`
 * once per frame, `detach()` on despawn and `dispose()` on teardown.
 */
export class RigAnimator {
  private readonly createMixer: RigMixerFactory;
  private readonly fadeSeconds: number;
  private readonly onWarn: (message: string, error?: unknown) => void;
  private readonly actionPolicy: Readonly<Record<RigAction, RigActionPolicy>>;
  private readonly records = new Map<number, AnimRecord>();
  private disposed = false;

  constructor(options: RigAnimatorOptions) {
    this.createMixer = options.createMixer;
    this.fadeSeconds = options.fadeSeconds ?? DEFAULT_FADE_SECONDS;
    this.onWarn = options.onWarn ?? defaultWarn;
    this.actionPolicy = options.actionPolicy ?? DEFAULT_ACTION_POLICY;
  }

  /**
   * Start animating one entity's rig. A rig that ships no usable clip is simply
   * not attached (it stays in bind pose — the graceful, never-blank rule), and a
   * re-attach for an id replaces the previous record so a rebuilt avatar never
   * leaves a mixer pointing at a disposed graph. Returns whether it attached.
   */
  attach(
    entityId: number,
    root: Object3D,
    clips: readonly RigClip[],
    speeds: LocomotionSpeeds = {},
  ): boolean {
    if (this.disposed) return false;
    const clipNames = clips.map((clip) => clip.name);
    const clipMap = resolveClipMap(clipNames);
    if (!hasAnyClip(clipMap)) return false; // nothing plausible to play ⇒ stay static

    const prior = this.records.get(entityId);
    this.detach(entityId); // replace any previous rig for this entity, disposing its mixer

    let mixer: RigMixerHandle | null;
    try {
      mixer = this.createMixer(root, clips);
    } catch (error) {
      this.onWarn(`mixer construction failed (entity ${entityId})`, error);
      return false; // never throw into the frame loop
    }
    if (mixer === null) return false;

    const record: AnimRecord = {
      mixer,
      clips: clipMap,
      actionClips: resolveActionClipMap(clipNames),
      current: null,
      action: null,
      actionPolicy: null,
      clipNames: new Set(clipNames),
      actionSeq: 0,
      speeds,
      walkClip: resolveClipFor('move', clipNames, WALK_ONLY_PATTERNS),
      moveShown: null,
      // The entity's ground speed outlives its mixer: a re-attach (the low tier's late clip
      // packs, a rebuilt rig) must not read 0 m/s mid-stride and show the walk at the floor rate.
      tracker: prior?.tracker ?? new GroundSpeedTracker(),
    };
    this.records.set(entityId, record);
    if (prior?.current) this.resume(entityId, record, prior.current);
    return true;
  }

  /**
   * Show `state` on a just-attached mixer at once — no fade up from bind pose — at the
   * entity's current speed, so a re-attach mid-stride does not visibly restart the stride.
   */
  private resume(entityId: number, record: AnimRecord, state: AnimState): void {
    const shown = this.clipForState(record, state);
    if (shown === null) return;
    try {
      record.mixer.play(shown, 0);
      record.current = state;
      record.moveShown = state === 'move' ? shown : null;
      const rate =
        state === 'move' ? locomotionRate(record.tracker.speed, record.speeds[shown]) : null;
      if (rate !== null) record.mixer.setLoopRate?.(rate);
    } catch (error) {
      this.onWarn(`clip '${shown}' failed to resume (entity ${entityId})`, error);
    }
  }

  /**
   * Feed the entity's RENDERED ground position for this frame
   * (`q-locomotion-speed-matched-playback`). While its state is `move` and its rig carries
   * authored ground speeds, the travelling clip plays at `speed ÷ authored speed` (clamped)
   * so the feet stay planted, and a rig with a walk and a run shows whichever fits the
   * speed. Call AFTER `setState` for the frame. Cheap: a no-op beyond the smoother for any
   * entity without speed data or not moving. Never throws into the frame loop.
   */
  observeGround(entityId: number, x: number, z: number, dtMs: number): void {
    const record = this.records.get(entityId);
    if (record === undefined) return;
    const speed = record.tracker.observe(x, z, dtMs);
    if (record.current !== 'move' || record.moveShown === null) return;
    try {
      if (record.action === null) {
        const want = this.moveClipFor(record, speed);
        if (want !== record.moveShown) {
          record.mixer.play(want, this.fadeSeconds);
          record.moveShown = want;
        }
      }
      const rate = locomotionRate(speed, record.speeds[record.moveShown]);
      if (rate !== null) record.mixer.setLoopRate?.(rate);
    } catch (error) {
      this.onWarn(`locomotion rate failed (entity ${entityId})`, error);
    }
  }

  /** The smoothed rendered ground speed of an entity, m/s (tests + `__gcSceneDump`). */
  groundSpeed(entityId: number): number | null {
    return this.records.get(entityId)?.tracker.speed ?? null;
  }

  /** The travelling clip to show at `speed` (the resolved move clip unless the walk fits). */
  private moveClipFor(record: AnimRecord, speed: number): string {
    const moveClip = record.clips.move as string;
    return pickLocomotionClip(speed, moveClip, record.walkClip, record.speeds, record.moveShown);
  }

  /** The clip that presents `state` now (the speed-picked variant for `move`). */
  private clipForState(record: AnimRecord, state: AnimState): string | null {
    const clipName = record.clips[state];
    if (clipName === null || clipName === undefined) return null;
    return state === 'move' ? this.moveClipFor(record, record.tracker.speed) : clipName;
  }

  /**
   * Present the entity's wire animation state. A repeated state is a no-op, so
   * this is safe (and cheap) to call every frame from the render loop; a state
   * whose clip did not resolve keeps the current clip playing rather than
   * snapping to bind pose. While a one-shot action plays (AP5.S2) a change to a
   * NON-travelling state is recorded but not played — the action owns the pose
   * and the animator returns to the latest state when it ends — whereas a change
   * to a travelling state (`STATE_TRAVELS`) CUTS a non-exempt action to
   * locomotion at once: the server owns position, so the rig must run where the
   * entity runs rather than slide in a swing/crouch pose (movement wins).
   */
  setState(entityId: number, state: AnimState): void {
    const record = this.records.get(entityId);
    if (record === undefined || record.current === state) return;
    const clipName = record.clips[state];
    if (clipName === null || clipName === undefined) return; // unresolved ⇒ keep playing
    if (record.action !== null) {
      record.current = state; // remembered either way: it is the state to return to
      const policy = record.actionPolicy ?? this.actionPolicy[record.action];
      const cut = STATE_TRAVELS[state] && policy.interruptedByTravel;
      if (!cut) return; // deferred: applied by the action's completion / clear
      record.actionSeq += 1; // the cut clip's in-flight completion is now stale
      record.action = null;
      record.actionPolicy = null;
    }
    const shown = this.clipForState(record, state) ?? clipName;
    try {
      record.mixer.play(shown, this.fadeSeconds);
      record.current = state;
      record.moveShown = state === 'move' ? shown : null;
    } catch (error) {
      this.onWarn(`clip '${shown}' failed to play (entity ${entityId})`, error);
    }
  }

  /**
   * Play a one-shot action on the entity's rig (AP5.S2), layered over locomotion.
   * Returns whether it started: `false` for an unattached entity, for a rig that
   * carries no plausible clip for this action (locomotion keeps playing — never
   * bind pose), while `dead` holds (until `clearAction`), while the entity
   * TRAVELS and the action's policy says movement wins (locomotion keeps the
   * pose — a swing cut on its next frame would be a flash), or on a mixer
   * failure. Any other action already playing is replaced; the SAME action
   * restarts. The policy's `holdSeconds` reaches the mixer as `maxSeconds` and
   * its `timeScale` as `timeScale` — each only when the policy carries it, so a
   * mixer sees no key it did not have before this action declared one.
   */
  playAction(entityId: number, action: RigAction): boolean {
    const record = this.records.get(entityId);
    if (record === undefined) return false;
    const clipName = record.actionClips[action];
    if (clipName === null || clipName === undefined) return false; // rig lacks it ⇒ keep locomotion
    return this.startAction(entityId, record, action, clipName, this.actionPolicy[action]);
  }

  /**
   * Play a NAMED clip as `action` under an explicit policy (AD-636: an enemy's
   * per-attack `Attack_<motion>` clip, resolved by the caller from content). Same
   * rules and return contract as `playAction` — dead holds, movement wins unless the
   * policy says the lead-in is held still, a clip this rig does not carry is a
   * no-op — except that the clip and the policy are the caller's, not the resolved
   * defaults. The policy is kept for the clip's life, so a later travel transition
   * is judged by the policy the clip STARTED under.
   */
  playActionClip(
    entityId: number,
    action: RigAction,
    clipName: string,
    policy: Readonly<RigActionPolicy>,
  ): boolean {
    const record = this.records.get(entityId);
    if (record === undefined || !record.clipNames.has(clipName)) return false;
    return this.startAction(entityId, record, action, clipName, policy);
  }

  private startAction(
    entityId: number,
    record: AnimRecord,
    action: RigAction,
    clipName: string,
    policy: Readonly<RigActionPolicy>,
  ): boolean {
    if (record.action === 'dead') return false; // a corpse neither flinches nor swings
    const travelling = record.current !== null && STATE_TRAVELS[record.current];
    if (policy.interruptedByTravel && travelling && policy.startsWhileTravelling !== true) {
      return false; // travelling: movement wins, locomotion keeps the pose
    }
    const token = ++record.actionSeq;
    try {
      record.mixer.playOnce(clipName, this.fadeSeconds, {
        clamp: action === 'dead',
        ...(policy.holdSeconds !== undefined ? { maxSeconds: policy.holdSeconds } : {}),
        ...(policy.timeScale !== undefined ? { timeScale: policy.timeScale } : {}),
        onFinished: () => this.finishAction(entityId, token),
      });
      record.action = action;
      record.actionPolicy = policy;
      return true;
    } catch (error) {
      this.onWarn(
        `action '${action}' (clip '${clipName}') failed to play (entity ${entityId})`,
        error,
      );
      return false;
    }
  }

  /**
   * End the entity's action explicitly (AP5.S2) — the respawn that releases a
   * clamped `dead`, or any caller that wants locomotion back now. Cross-fades to
   * the current locomotion state. A no-op when no action is active.
   */
  clearAction(entityId: number): void {
    const record = this.records.get(entityId);
    if (record === undefined || record.action === null) return;
    record.actionSeq += 1; // an in-flight completion for the cleared clip is now stale
    record.action = null;
    record.actionPolicy = null;
    this.resumeLocomotion(entityId, record);
  }

  /** The action currently playing/holding for an entity, or `null` (tests + `__gcSceneDump`). */
  activeAction(entityId: number): RigAction | null {
    return this.records.get(entityId)?.action ?? null;
  }

  /**
   * Advance ONE entity's mixer by `seconds` without touching the others (AP5.S2):
   * the NPC placer de-phases a crowd's idle loops with a deterministic per-NPC
   * offset so they never breathe in unison. Guarded like `update`.
   */
  seek(entityId: number, seconds: number): void {
    const record = this.records.get(entityId);
    if (record === undefined || !(seconds > 0)) return;
    try {
      record.mixer.update(seconds);
    } catch (error) {
      this.onWarn(`mixer seek failed (entity ${entityId}); dropping`, error);
      this.records.delete(entityId);
    }
  }

  /**
   * Advance every attached mixer. `dtMs` is the frame delta in MILLISECONDS (the
   * unit `Game.frame()` computes); it is converted to the seconds three expects
   * here, at the single boundary, so callers never have to remember.
   *
   * `isHeld` (q-combat-game-feel hit stop): a mixer whose entity it names is not advanced this
   * frame, so that body holds its pose for the hit-stop beat; every other mixer runs on.
   */
  update(dtMs: number, isHeld?: (entityId: number) => boolean): void {
    if (this.disposed || this.records.size === 0) return;
    const dtSeconds = dtMs / 1000;
    for (const [entityId, record] of this.records) {
      if (isHeld?.(entityId) === true) continue;
      try {
        record.mixer.update(dtSeconds);
      } catch (error) {
        // One bad mixer must not stop the world: drop it and keep rendering.
        this.onWarn(`mixer update failed (entity ${entityId}); dropping`, error);
        this.records.delete(entityId);
      }
    }
  }

  /** Stop + drop one entity's animation (despawn, or an avatar rebuild). */
  detach(entityId: number): void {
    const record = this.records.get(entityId);
    if (record === undefined) return;
    this.records.delete(entityId);
    try {
      record.mixer.stop();
    } catch (error) {
      this.onWarn(`mixer stop failed (entity ${entityId})`, error);
    }
  }

  /** Stop every mixer (scene teardown). */
  dispose(): void {
    this.disposed = true;
    for (const id of [...this.records.keys()]) this.detach(id);
  }

  /** Attached entity count — test/diagnostic surface (`__gcSceneDump`). */
  get size(): number {
    return this.records.size;
  }

  /** A one-shot completed: hand the pose back to locomotion unless it is a held `dead`. */
  private finishAction(entityId: number, token: number): void {
    const record = this.records.get(entityId);
    if (record === undefined || record.actionSeq !== token || record.action === null) return;
    if (record.action === 'dead') return; // clamped: holds its last frame until clearAction
    record.action = null;
    record.actionPolicy = null;
    this.resumeLocomotion(entityId, record);
  }

  /**
   * Cross-fade back to the current locomotion clip. An entity that never received
   * a state falls back to `idle`, so a finished action never leaves a rig frozen on
   * its last frame (never-blank); a rig with no clip for it keeps the held pose.
   */
  private resumeLocomotion(entityId: number, record: AnimRecord): void {
    const state = record.current ?? 'idle';
    const clipName = this.clipForState(record, state);
    if (clipName === null) return;
    try {
      record.mixer.play(clipName, this.fadeSeconds);
      record.current = state;
      record.moveShown = state === 'move' ? clipName : null;
    } catch (error) {
      this.onWarn(`clip '${clipName}' failed to resume (entity ${entityId})`, error);
    }
  }
}
